/**
 * <s-welcome-popup> — welcome offer pop-up controller.
 *
 * Opens the child <dialog> once per visitor after `delay` seconds. After a
 * successful customer signup (Shopify redirects back with customer_posted=true)
 * it reopens immediately in its success state to reveal the discount code. A
 * signup error reopens it on the form so the visitor sees the message.
 * Closing stores a timestamp so it stays hidden for `days-hidden` days.
 *
 * Attributes:
 *   delay       — seconds before the first open (default 5)
 *   days-hidden — days to stay hidden after closing (default 30)
 *   preview     — editor preview: always open on load, never remembers dismissal
 *   disabled    — never auto-open (still previewable in the editor)
 *
 * Commands (on the dialog):
 *   --copy-code — copy the discount code to the clipboard
 */
const STORAGE_KEY = 'welcome-popup-dismissed-at';
const DAY_MS = 24 * 60 * 60 * 1000;

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer;
  #dialog;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const preview = this.hasAttribute('preview');

    this.#dialog.addEventListener('command', (e) => {
      if (e.command === '--copy-code') {
        e.preventDefault();
        this.#copyCode(e.source);
      }
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (!preview) this.#remember();
    }, opts);

    const params = new URLSearchParams(window.location.search);
    const hasError = Boolean(this.querySelector('[data-welcome-signup] [role="alert"]'));
    const signedUp = params.get('customer_posted') === 'true' && !hasError;

    if (signedUp) {
      this.#showSuccess();
      this.#open();
      return;
    }

    if (hasError) {
      this.#open();
      return;
    }

    if (preview) {
      this.#open();
      return;
    }

    if (this.hasAttribute('disabled') || this.#recentlyDismissed()) return;

    const delay = Math.max(0, Number(this.getAttribute('delay')) || 0) * 1000;
    this.#timer = window.setTimeout(() => this.#openWhenIdle(), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #openWhenIdle() {
    // Never interrupt another open modal such as the cart drawer or search.
    if (document.querySelector('dialog:modal')) {
      this.#timer = window.setTimeout(() => this.#openWhenIdle(), 4000);
      return;
    }
    this.#open();
  }

  #open() {
    if (this.#dialog.open) return;
    try {
      this.#dialog.showModal();
    } catch {
      // The dialog is disconnected or another modal is blocking; skip quietly.
    }
  }

  #showSuccess() {
    const signup = this.querySelector('[data-welcome-signup]');
    const success = this.querySelector('[data-welcome-success]');
    if (signup) signup.hidden = true;
    if (success) success.hidden = false;
  }

  #recentlyDismissed() {
    try {
      const at = Number(window.localStorage.getItem(STORAGE_KEY));
      if (!at) return false;
      const days = Number(this.getAttribute('days-hidden')) || 30;
      return Date.now() - at < days * DAY_MS;
    } catch {
      return false;
    }
  }

  #remember() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode); the pop-up may show again next visit.
    }
  }

  async #copyCode(source) {
    const code = this.querySelector('[data-welcome-code]')?.textContent?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    const label = source?.querySelector('[data-copy-label]');
    const copied = source?.dataset.copiedLabel;
    const status = this.querySelector('[data-copy-status]');
    if (status && copied) status.textContent = copied;
    if (label && copied) {
      const original = label.textContent;
      label.textContent = copied;
      window.setTimeout(() => { label.textContent = original; }, 2000);
    }
  }
}

customElements.define('s-welcome-popup', SWelcomePopup);
